import { NetworkStatus, gql, useQuery } from '@apollo/client';
import {
  Box,
  Divider,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Spacer,
  Stack,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useRouter } from 'next/router';
import * as React from 'react';

import { MoreFilters } from '@/components/MoreFilters';
import { NavigationButton } from '@/components/NavigationButton';
import { EAttribute_InputType, EAttributeOwnerKind } from '@/graphql-types/globalTypes';
import { useCurrent } from '@/hooks/useCurrent';
import { useWindowHeight } from '@/hooks/useWindowHeight';
import { Card } from '@/imports/ui/chakra/components/Card';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { UnexpectedError } from '@/imports/ui/chakra/components/UnexpectedError';
import { SearchIcon } from '@/imports/ui/chakra/feather';
import { SearchTips } from '@/imports/ui/components/algolia/SearchTips';
import { useDebouncedValue } from '@/imports/ui/hooks/useDebouncedValue';
import { Blueprint, useBlueprint } from '@/layouts/components/Blueprint';

import { Hits } from './components/Hits';
import { SponsorDirectorySelectedFilters } from './components/SponsorDirectorySelectedFilters';
import {
  EXPLORE_PAGE_SIZE,
  FOLLOWING_PAGE_SIZE,
  SPONSOR_DIRECTORY_FREE_TEXT_SEARCH_DEBOUNCE_MS,
  SPONSOR_DIRECTORY_SEARCH_DEBOUNCE_MS,
} from './constants';
import { SponsorDirectory_FilterOptions } from './graphql-types/SponsorDirectory_FilterOptions';
import {
  SponsorDirectory_Search,
  SponsorDirectory_SearchVariables,
} from './graphql-types/SponsorDirectory_Search';
import {
  buildSponsorDirectorySearchFilters,
  useSponsorDirectoryFilters,
} from './hooks/useSponsorDirectoryFilters';
import { SponsorDirectoryFilterOptions } from './types';
import { SponsorIcon } from '@/imports/ui/chakra/customIcon/SponsorIcon';
import { FollowingIcon } from '@/imports/ui/chakra/customIcon/FollowingIcon';


export const SPONSOR_DIRECTORY_SEARCH = gql`
  query SponsorDirectory_Search($input: SponsorDirectorySearchInput!) {
    sponsorDirectorySearch(input: $input) {
      totalCount
      results {
        _id
        name
        aboutUsPlainText
        profilePictureUrl
        isFollowing
        sponsorTier {
          name
          order
        }
        company {
          website
          phoneNumber
          address {
            streetAddress
            city
            state
            country
            zipCode
          }
        }
        cardAttributes {
          attributeSchemaId
          name
          displayValues
        }
      }
    }
  }
`;

export const SPONSOR_DIRECTORY_FILTER_OPTIONS = gql`
  query SponsorDirectory_FilterOptions {
    sponsorDirectoryFilterOptions {
      sponsorTierNames
      attributeFilters {
        _id
        name
        inputType
        options {
          label
        }
      }
    }
  }
`;

type DirectoryTab = 'following' | 'explore';

const scrollbar = {
  '::-webkit-scrollbar': {
    display: 'none',
  },
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
};

const SponsorNav: React.FC = ({ children }) => {
  const windowHeight = useWindowHeight();
  const { headerHeight } = useBlueprint();

  return (
    <Box
      alignSelf="flex-start"
      as="nav"
      display={{ base: 'none', lg: 'block' }}
      h={`${windowHeight - headerHeight}px`}
      mx="-1"
      overflowY="auto"
      position="sticky"
      px="1"
      py="4"
      top={`${headerHeight}px`}
      w={{ base: 60, xl: 72 }}
      sx={scrollbar}
    >
      {children}
    </Box>
  );
};

const SponsorMain: React.FC = ({ children }) => {
  return (
    <Box as="main" py="4" px="1" mx="-1" flex="1" overflowY="auto">
      {children}
    </Box>
  );
};

const SponsorAside: React.FC = () => {
  const windowHeight = useWindowHeight();
  const { headerHeight } = useBlueprint();

  const height = useBreakpointValue({
    base: windowHeight - headerHeight,
    lg: windowHeight - headerHeight,
  });

  return (
    <Box
      alignSelf="flex-start"
      as="aside"
      display={{ base: 'none', md: 'block' }}
      h={`${height}px`}
      mx="-1"
      overflowY="auto"
      position="sticky"
      px="1"
      py="4"
      top={`${headerHeight}px`}
      w={{ base: 72, lg: 60, xl: 72 }}
      sx={scrollbar}
    />
  );
};

export const SponsorDirectory: React.FC = () => {
  const router = useRouter();
  const current = useCurrent();

  const tabFromQuery = router.query.tab as DirectoryTab;
  const [activeTab, setActiveTab] = React.useState<DirectoryTab>(
    tabFromQuery === 'following' ? 'following' : 'explore'
  );

  const [searchText, setSearchText] = React.useState('');
  const [freeTextValues, setFreeTextValues] = React.useState<
    Record<string, string>
  >({});
  const debouncedSearchText = useDebouncedValue(
    searchText,
    SPONSOR_DIRECTORY_SEARCH_DEBOUNCE_MS
  );
  const debouncedFreeTextValues = useDebouncedValue(
    freeTextValues,
    SPONSOR_DIRECTORY_FREE_TEXT_SEARCH_DEBOUNCE_MS
  );

  const [page, setPage] = React.useState(1);
  const [accumulatedSponsors, setAccumulatedSponsors] = React.useState<
    SponsorDirectory_Search['sponsorDirectorySearch']['results']
  >([]);

  const pageSize =
    activeTab === 'following' ? FOLLOWING_PAGE_SIZE : EXPLORE_PAGE_SIZE;

  const filterOptionsQuery = useQuery<SponsorDirectory_FilterOptions>(
    SPONSOR_DIRECTORY_FILTER_OPTIONS,
    {
      fetchPolicy: 'cache-first',
    }
  );

  const filterOptions: SponsorDirectoryFilterOptions | undefined =
    filterOptionsQuery.data?.sponsorDirectoryFilterOptions
      ? {
          sponsorTierNames:
            filterOptionsQuery.data.sponsorDirectoryFilterOptions
              .sponsorTierNames,
          attributeFilters:
            filterOptionsQuery.data.sponsorDirectoryFilterOptions.attributeFilters.map(
              (attributeFilter) => ({
                _id: attributeFilter._id,
                name: attributeFilter.name,
                inputType: attributeFilter.inputType as EAttribute_InputType,
                options: attributeFilter.options.map((option) => ({
                  label: option.label,
                })),
              })
            ),
        }
      : undefined;

  const {
    selectedFilters,
    unselectedFilters,
    selectFilter,
    unselectFilter: unselectFilterBase,
  } = useSponsorDirectoryFilters(filterOptions, filterOptionsQuery.loading);

  const setFreeTextValue = React.useCallback(
    (attributeSchemaId: string, value: string) => {
      setFreeTextValues((previous) => ({
        ...previous,
        [attributeSchemaId]: value,
      }));
    },
    []
  );

  const unselectFilter = React.useCallback(
    (params: { attributeSchemaId: string }) => {
      unselectFilterBase(params);
      setFreeTextValues((previous) => {
        if (!(params.attributeSchemaId in previous)) {
          return previous;
        }
        const next = { ...previous };
        delete next[params.attributeSchemaId];
        return next;
      });
    },
    [unselectFilterBase]
  );

  const { sponsorTierName, attributeFilters } = React.useMemo(
    () =>
      buildSponsorDirectorySearchFilters(
        selectedFilters,
        debouncedFreeTextValues
      ),
    [selectedFilters, debouncedFreeTextValues]
  );

  const attributeFiltersKey = React.useMemo(
    () => JSON.stringify(attributeFilters ?? null),
    [attributeFilters]
  );

  React.useEffect(() => {
    setPage(1);
    setAccumulatedSponsors([]);
  }, [activeTab, debouncedSearchText, attributeFiltersKey, sponsorTierName]);

  const searchVariables =
    React.useMemo<SponsorDirectory_SearchVariables>(
      () => ({
        input: {
          searchText: debouncedSearchText === '' ? null : debouncedSearchText,
          sponsorTierName: sponsorTierName ?? null,
          attributeFilters,
          followingOnly: activeTab === 'following',
          limit: pageSize,
          offset: (page - 1) * pageSize,
          includeIsFollowing: true,
        },
      }),
      [
        activeTab,
        attributeFilters,
        page,
        debouncedSearchText,
        pageSize,
        sponsorTierName,
      ]
    );

  const sponsorsQuery = useQuery<
    SponsorDirectory_Search,
    SponsorDirectory_SearchVariables
  >(SPONSOR_DIRECTORY_SEARCH, {
    variables: searchVariables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });

  const searchData =
    sponsorsQuery.networkStatus === NetworkStatus.setVariables
      ? sponsorsQuery.previousData?.sponsorDirectorySearch
      : sponsorsQuery.data?.sponsorDirectorySearch;

  const sponsors = React.useMemo(
    () => searchData?.results ?? [],
    [searchData?.results]
  );
  const isLoading =
    sponsorsQuery.loading ||
    sponsorsQuery.networkStatus === NetworkStatus.setVariables;
  const displayedSponsors =
    accumulatedSponsors.length > 0
      ? accumulatedSponsors
      : isLoading
        ? []
        : sponsors;
  const totalCount = searchData?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  React.useLayoutEffect(() => {
    if (sponsorsQuery.networkStatus === NetworkStatus.setVariables) {
      return;
    }

    if (page === 1) {
      setAccumulatedSponsors(sponsors);
      return;
    }

    setAccumulatedSponsors((previous) => {
      const existingIds = new Set(previous.map((sponsor) => sponsor._id));
      const newSponsors = sponsors.filter(
        (sponsor) => !existingIds.has(sponsor._id)
      );
      return [...previous, ...newSponsors];
    });
  }, [sponsorsQuery.networkStatus, page, sponsors]);

  const handleTabChange = (newTab: DirectoryTab) => {
    setActiveTab(newTab);
    router.replace(
      {
        pathname: router.pathname,
        query: { ...router.query, tab: newTab },
      },
      undefined,
      { shallow: true }
    );
  };

  React.useEffect(() => {
    if (tabFromQuery === 'explore' || tabFromQuery === 'following') {
      setActiveTab(tabFromQuery);
    }
  }, [tabFromQuery]);

  React.useEffect(() => {
    if (page > totalPages) {
      setPage(1);
    }
  }, [page, totalPages]);

  const isInitialLoading =
    sponsorsQuery.loading &&
    accumulatedSponsors.length === 0 &&
    !sponsorsQuery.data &&
    !sponsorsQuery.previousData;

  const hasMore = page < totalPages;

  const loadMore = React.useCallback(() => {
    if (
      page < totalPages &&
      !isLoading &&
      accumulatedSponsors.length > 0
    ) {
      setPage((current) => current + 1);
    }
  }, [page, totalPages, isLoading, accumulatedSponsors.length]);

  const isLoadingMore = isLoading && displayedSponsors.length > 0;

  if (isInitialLoading) {
    return (
      <Blueprint>
        <Loading />
      </Blueprint>
    );
  }

  if (sponsorsQuery.error) {
    return (
      <Blueprint>
        <UnexpectedError />
      </Blueprint>
    );
  }

  return (
    <Blueprint>
      <Box
        display="flex"
        gap={{ base: 2, md: 4 }}
        maxW="container.xl"
        mx="auto"
        px="4"
      >
        <SponsorNav>
          <Stack>
            <NavigationButton
              href={`${router.pathname}?tab=explore`}
              isActive={activeTab === 'explore'}
              onClick={() => handleTabChange('explore')}
              newView={true}
            > 
            <SponsorIcon boxSize="5" mr="2" color="primary.500"/>
              Explore Sponsors
            </NavigationButton>
            <NavigationButton
              href={`${router.pathname}?tab=following`}
              isActive={activeTab === 'following'}
              onClick={() => handleTabChange('following')}
              newView={true}
            >
            <FollowingIcon boxSize="5" mr="2" color="primary.500" /> 
              Following
            </NavigationButton>
          </Stack>
        </SponsorNav>
        <SponsorMain>
          <Box w="100%" maxW="container.md">
            <Stack spacing="4">
              <Card>
                <Stack spacing="0" divider={<Divider />}>
                  <Flex py="4" pl="4">
                    <InputGroup flex="1">
                      <InputLeftElement pointerEvents="none">
                        <SearchIcon color="primary.500" />
                      </InputLeftElement>
                      <Input
                        variant="outline"
                        borderRadius="md"
                        placeholder={`Search ${current.tenant?.organizationEntityName ?? 'sponsors'}`}
                        type="search"
                        value={searchText}
                        onChange={(event) => setSearchText(event.target.value)}
                      />
                    </InputGroup>
                    <SearchTips ownerKind={EAttributeOwnerKind.ORGANIZATION} />
                  </Flex>
                  {(selectedFilters.length > 0 ||
                    unselectedFilters.length > 0) && (
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing="4" p="4">
                      <SponsorDirectorySelectedFilters
                        selectedFilters={selectedFilters}
                        unselectFilter={unselectFilter}
                        selectFilter={selectFilter}
                        freeTextValues={freeTextValues}
                        onFreeTextValueChange={setFreeTextValue}
                      />
                      <Flex direction="column">
                        <Spacer />
                        <MoreFilters
                          unselectedFilters={unselectedFilters}
                          selectedFilters={selectedFilters}
                          selectFilter={selectFilter}
                          unselectFilter={unselectFilter}
                        />
                      </Flex>
                    </SimpleGrid>
                  )}
                </Stack>
              </Card>

              <Card px="4" py="6" pos="relative">
                <Text color="gray.700">
                  {activeTab === 'following'
                    ? ''
                    : `Showing ${totalCount.toLocaleString()} Result${
                        totalCount === 1 ? '' : 's'
                      }`}
                </Text>
                <Hits
                  sponsors={displayedSponsors}
                  activeTab={activeTab}
                  loading={isLoading && displayedSponsors.length === 0}
                  isFollowingBadge={activeTab === 'explore'}
                  hasMore={hasMore}
                  isLoadingMore={isLoadingMore}
                  onLoadMore={loadMore}
                />
              </Card>
            </Stack>
          </Box>
        </SponsorMain>
        <SponsorAside />
      </Box>
    </Blueprint>
  );
};